import React from 'react';
import EditableTextField from './EditableTextField';
import EditableSelectField from './EditableSelectField';

export default function ProjectOverviewTab({ project, editable, handleInputChange, policyMap, defaultPolicyType }) {
  return (
    <table className='project-detail-table'>
      <tbody>
        <tr>
          <td>Private Project Name</td>
          <td>
            <EditableTextField
              name='private_project_name'
              value={project.private_project_name}
              onChange={handleInputChange}
              editable={editable === true}
            />
          </td>
        </tr>
        <tr>
          {/* picklist */}
          <td>Project Type</td>
          <td>
            <EditableSelectField
              name='project_type'
              value={project.project_type}
              onChange={handleInputChange}
              editable={editable === true}
              disabledFallback='text'
              options={['New Construction', 'Retrofit', 'Addition']}
            />
          </td>
        </tr>
        <tr>
          <td>Building Function</td>
          <td>
            <EditableSelectField
              name='building_function'
              value={project.building_function}
              onChange={handleInputChange}
              editable={editable === true}
              disabledFallback='text'
              options={[
                'Single-Family',
                'Multifamily',
                'Mixed-Use',
                'School / Education',
                'Office',
                'Government / Public',
                'Hotel',
                'Dormitory',
                'Other Non-Residential',
              ]}
            />
          </td>
        </tr>
        <tr>
          <td>Architecture Planning</td>
          <td>
            <EditableTextField
              name='architecture_planning'
              value={project.architecture_planning}
              onChange={handleInputChange}
              editable={editable === true}
            />
          </td>
        </tr>
        <tr>
          <td>Mechanical Systems Designer</td>
          <td>
            <EditableTextField
              name='mechanical_systems_designer'
              value={project.mechanical_systems_designer}
              onChange={handleInputChange}
              editable={editable === true}
            />
          </td>
        </tr>
        <tr>
          <td>Additional Consultants</td>
          <td>
            <EditableTextField
              name='additional_consultants'
              value={project.additional_consultants}
              onChange={handleInputChange}
              editable={editable === true}
            />
          </td>
        </tr>
        <tr>
          {/* picklist */}
          <td>Project Delivery Method</td>
          <td>
            <EditableSelectField
              name='project_delivery_method'
              value={project.project_delivery_method}
              onChange={handleInputChange}
              editable={editable === true}
              disabledFallback='text'
              options={[
                'Design-Bid-Build (DBB)',
                'Design Build (DB)',
                'Construction Manager at Risk (CMAR)',
                'Construction Management Multi-Prime (CMMP)',
                'Integrated Project Delivery (IPD)',
              ]}
            />
          </td>
        </tr>
        <tr>
          <td>Developer</td>
          <td>
            <EditableTextField
              name='developer'
              value={project.developer}
              onChange={handleInputChange}
              editable={editable === true}
            />
          </td>
        </tr>
        <tr>
          <td>Affiliate</td>
          <td>
            <EditableTextField
              name='affiliate'
              value={project.affiliate}
              onChange={handleInputChange}
              editable={editable === true}
            />
          </td>
        </tr>
        {project.market_category !== undefined ? (
          <tr>
            {/* picklist */}
            <td>Market Category</td>
            <td>
              <EditableSelectField
                name='market_category'
                value={project.market_category}
                onChange={handleInputChange}
                editable={editable === true}
                disabledFallback='text'
                options={[
                  'Supportive Housing',
                  'Low Income Housing',
                  'Affordable Housing',
                  'Market Rate',
                ]}
              />
            </td>
          </tr>
        ) : (
          ''
        )}
        {/* Policy Type: not folded into EditableSelectField — editable and disabled
            branches intentionally read different fields (policy_type_crm_id to drive
            the live policyMap select vs. policy_type for display), not a bug. */}
        <tr>
          <td>Policy Type</td>
          <td>
            {editable === true ? (
              <select
                name='policy_type_crm_id'
                onChange={(e) => handleInputChange(e)}
                className='edit-input'
                defaultValue={defaultPolicyType?.crm_id}
              >
                <option value='' />
                {policyMap}
              </select>
            ) : (
              <input
                name='policy_type'
                onChange={(e) => handleInputChange(e)}
                className='project-detail-input'
                disabled
                value={project.policy_type}
              />
            )}
          </td>
        </tr>
        {/* Total Project Cost: not folded — the disabled branch has no onChange and no
            type='number' (unlike the editable branch); EditableTextField assumes both
            branches share type/onChange, so converting would silently add attributes
            the source never had. Harmless on a disabled input, but left hand-written
            rather than silently normalized. */}
        <tr>
          {/* number */}
          <td>Total Project Cost</td>
          <td>
            ${' '}
            {editable === true ? (
              <input
                type='number'
                className='edit-input'
                onChange={(e) => handleInputChange(e)}
                name='total_project_cost'
                value={project.total_project_cost}
              />
            ) : (
              <input
                className='project-detail-input'
                disabled
                name='total_project_cost'
                value={project.total_project_cost}
              />
            )}{' '}
            USD
          </td>
        </tr>
        <tr>
          <td>Construction Completion Year</td>
          <td>
            <EditableTextField
              name='construction_completion_year'
              value={project.construction_completion_year}
              onChange={handleInputChange}
              editable={editable === true}
            />
          </td>
        </tr>
        {/* All-Electric / Monitored Data checkboxes: fixed 2026-07-10 — real ZOHO data confirmed
            boolean (not 'Yes'/'No' string), so `checked={!!project.X}` + a real onChange are the
            correct binding. History: DEV_NOTES/FOUND_WORK/all-electric-monitored-data-checkboxes-broken.md. */}
        <tr>
          {/* checkbox */}
          <td>All-Electric</td>
          <td>
            {editable === true ? (
              <input
                type='checkbox'
                className='edit-input'
                name='all_electric'
                checked={!!project.all_electric}
                onChange={handleInputChange}
              />
            ) : (
              <input
                type='checkbox'
                className='project-detail-input'
                disabled
                name='all_electric'
                checked={!!project.all_electric}
                onChange={handleInputChange}
              />
            )}
          </td>
        </tr>
        <tr>
          <td>Monitored Data</td>
          <td>
            {editable === true ? (
              <input
                type='checkbox'
                className='edit-input'
                name='monitored_data_available'
                checked={!!project.monitored_data_available}
                onChange={handleInputChange}
              />
            ) : (
              <input
                type='checkbox'
                className='project-detail-input'
                disabled
                name='monitored_data_available'
                checked={!!project.monitored_data_available}
                onChange={handleInputChange}
              />
            )}
          </td>
        </tr>
        {/* Monitored Data Report: not folded — name='' is an unbound placeholder stub
            (not wired to any project field, predates this refactor); left as-is. */}
        <tr>
          <td>Monitored Data Report</td>
          <td>
            {editable === true ? (
              <input className='edit-input' name='' value='' />
            ) : (
              <input
                className='project-detail-input'
                disabled
                name=''
                value=''
              />
            )}
          </td>
        </tr>
        {/* Additional Notes: not folded — uses <textarea>, not <input>;
            EditableTextField only models the input case. */}
        <tr>
          <td>Additional Notes</td>
          <td>
            {editable === true ? (
              <textarea
                className='edit-input'
                name='additional_notes'
                value={project.additional_notes}
              />
            ) : (
              <textarea
                className='project-detail-input'
                disabled
                name='additional_notes'
                value={project.additional_notes}
              />
            )}
          </td>
        </tr>
        <tr>
          <td>Building Owner</td>
          <td>
            <EditableTextField
              name='building_owner'
              value={project.building_owner ?? ''}
              onChange={handleInputChange}
              editable={editable === true}
            />
          </td>
        </tr>
        <tr>
          <td>Construction Company</td>
          <td>
            <EditableTextField
              name='construction_company'
              value={project.construction_company ?? ''}
              onChange={handleInputChange}
              editable={editable === true}
            />
          </td>
        </tr>
      </tbody>
    </table>
  );
}
